// In-app navigation shell. A horizontal Polaris tab bar driven by React Router,
// so the four sections are reachable inside the embedded iframe. App Bridge
// (mounted in App.tsx) supplies session tokens; this frame only handles routing.

import { Tabs } from "@shopify/polaris";
import { useCallback, useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const TABS = [
  { id: "dashboard", content: "Overview", path: "/" },
  { id: "content", content: "Content", path: "/content" },
  { id: "revenue", content: "Revenue", path: "/revenue" },
  { id: "settings", content: "Settings", path: "/settings" },
];

export function AppFrame({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const selected = useMemo(() => {
    // Longest matching prefix wins so /content/:id keeps the Content tab active.
    const matches = TABS.map((t, i) => ({ i, path: t.path }))
      .filter(({ path }) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path)))
      .sort((a, b) => b.path.length - a.path.length);
    return matches.length ? matches[0].i : 0;
  }, [location.pathname]);

  const handleSelect = useCallback(
    // Preserve the current query string (host/shop/embedded/preview) on every
    // in-app navigation. React Router's navigate() defaults to an empty search,
    // which would drop the Shopify `host` param — so a refresh or copied URL
    // afterwards lands on SetupNotice and the embedded app can't boot.
    (index: number) => navigate({ pathname: TABS[index].path, search: location.search }),
    [navigate, location.search],
  );

  return (
    <Tabs
      tabs={TABS.map((t) => ({ id: t.id, content: t.content }))}
      selected={selected}
      onSelect={handleSelect}
      fitted
    >
      {children}
    </Tabs>
  );
}
